import { Component, OnInit, Input, OnChanges, SimpleChanges, EventEmitter, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormControl } from '@angular/forms';
import { AllServicesService } from '../../../services/all-services.service';
import { SharedServiceService } from '../../services/shared-service.service';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { startWith, map } from 'rxjs/operators';
import { filter, firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-bc-fob-m-viscosity-modifier',
  imports: [CommonModule, FormsModule, ReactiveFormsModule,
    ScrollingModule, MatFormFieldModule, MatInputModule, MatAutocompleteModule],
  templateUrl: './bc-fob-m-viscosity-modifier.component.html',
  styleUrl: './bc-fob-m-viscosity-modifier.component.scss'
})
export class BcFobMViscosityModifierComponent implements OnInit, OnChanges {
  @Input() vmData: any = null; // Retrieve data from parent
  @Output() vmSelectedData = new EventEmitter<any[]>();
  rows: any[] = [];
  data: any[] = [null, null];

  vmOptions: any[] = []; // List of options for the autocomplete
  filteredVmOptions: any[][] = []; // Array of arrays to keep filtered options per row
  vmControls: FormControl[] = []; // Array of form controls for each row

  // To keep the previous valid selections
  lastValidSelections: any[] = [];


  constructor(private dataService: AllServicesService,
    private sharedService: SharedServiceService
  ) { }

  private formatToTwoDecimals(value: unknown): string {
    if (value === null || value === undefined || value === '') {
      return '';
    }
    const parsed = typeof value === 'string' ? parseFloat(value) : Number(value);
    if (!Number.isFinite(parsed)) {
      return '';
    }
    const rounded = Math.round((parsed + Number.EPSILON) * 100) / 100;
    return rounded.toFixed(2);
  }

  private sanitizeNumericInputValue(value: string): string {
    if (!value) {
      return '';
    }
    const cleaned = value.replace(/[^0-9.]/g, '');
    const parts = cleaned.split('.');
    if (parts.length <= 1) {
      return cleaned;
    }
    return `${parts[0]}.${parts.slice(1).join('')}`;
  }

  restrictNumericInput(event: KeyboardEvent): void {
    const allowedKeys = [
      'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
      'Tab', 'Enter', 'Home', 'End'
    ];
    if (allowedKeys.includes(event.key)) {
      return;
    }
    if (!/^[0-9.]$/.test(event.key)) {
      event.preventDefault();
    }
  }

  handleTreatInput(event: Event, rowIndex: number, field: 'treat1' | 'treat2'): void {
    const target = event.target as HTMLInputElement;
    const cleaned = this.sanitizeNumericInputValue(target.value);
    if (cleaned !== target.value) {
      target.value = cleaned;
    }
    this.rows[rowIndex][field] = cleaned;
    this.limitTreatValue(rowIndex, field);
  }

  onTreatBlur(rowIndex: number, field: 'treat1' | 'treat2'): void {
    const formatted = this.formatToTwoDecimals(this.rows[rowIndex][field]);
    this.rows[rowIndex][field] = formatted;
    this.calculateAndEmitTotals();
  }

  ngOnInit(): void {
    this.addRow();
    this.addRow();

    // Hydrate VM selections when a favorite is imported
    this.sharedService.ImpFavoriteFlag$
      .pipe(filter(flag => flag === true))
      .subscribe(() => this.hydrateVmFromImport());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['vmData'] && this.vmData) {
      this.processDropdownData();
    }
  }

  processDropdownData(): void {
    if (this.vmData) {
      // Transform data for autocomplete
      this.vmOptions = this.vmData.map((item: any) => ({
        label: item.Additive,
        value: item.Additive,
        data: item
      })).sort((a: any, b: any) => this.compareByLabel(a, b));

      // Initialize filteredVmOptions for each row
      this.rows.forEach((row, index) => {
        this.filteredVmOptions[index] = [...this.vmOptions];
      });
    }
  }

  // Method to filter options based on the entered text
  private _filter(value: string, rowIndex: number): any[] {
    const filterValue = value.toLowerCase();
    return this.vmOptions.filter(option =>
      option.label.toLowerCase().includes(filterValue)
    ).sort((a: any, b: any) => this.compareByLabel(a, b));
  }

  private compareByLabel(a: any, b: any): number {
    const aLabel = String(a?.label ?? '');
    const bLabel = String(b?.label ?? '');
    return aLabel.localeCompare(bLabel, undefined, { sensitivity: 'base' });
  }

  // Method to display the text in the autocomplete
  displayFn(item: any): string {
    return item && item.label ? item.label : '';
  }

  // When the input loses focus
  onVmInputBlur(rowIndex: number): void {
    const currentValue = this.vmControls[rowIndex].value;
    if (!this.isValidVmSelection(currentValue)) {
      this.vmControls[rowIndex].setValue(this.lastValidSelections[rowIndex], { emitEvent: false });
    }
  }

  // Validates whether the current value is a valid selection
  private isValidVmSelection(value: any): boolean {
    if (!value) return true; // Allow empty values

    // If it is an object with a value property
    if (typeof value === 'object' && value.value) {
      return this.vmOptions.some(option => option.value === value.value);
    }

    return false; // If it is a string or another type, it is not valid
  }

  // Method for when an option is selected
  onVmSelected(event: any, rowIndex: number): void {
    const selectedVm = event.option.value;

    // Save the valid selection
    this.lastValidSelections[rowIndex] = selectedVm;

    // Update the control
    this.vmControls[rowIndex].setValue(selectedVm, { emitEvent: false });

    // Update the row
    this.rows[rowIndex].selectedValue = selectedVm.value;

    // Get the additional data
    const vmSelection = this.rows.map(r => r.selectedValue ? r.selectedValue : null);
    this.getVMData(vmSelection);
  }

  emitSelectedData() {
    this.sharedService.setVmSelectedData(this.rows);
  }

  handleInputChange(event: Event, property: string): void {
    this.emitSelectedData();
  }

  onSelectionChange() {
    const vmSelection = this.rows.map(r => r.selectedValue ? r.selectedValue : null);
    this.getVMData(vmSelection);
    this.emitSelectedData();
  }

  getVMData(vmSelection: string[]) {
    const data = { data: vmSelection };

    this.dataService.get_vm_data(data).subscribe(
      (response) => {
        if (response) {
          this.updateRowsWithVMData(response);
        }
      },
      (error) => {
        console.error('Error fetching VM data', error);
      }
    );
  }

  updateRowsWithVMData(data: any) {
    if (typeof data === 'string') {
      try {
        data = JSON.parse(data);
      } catch (e) {
        console.error('No se pudo parsear data:', data);
        return;
      }
    }
    if (Array.isArray(data)) {
      data.forEach((item, i) => {
        if (i < this.rows.length) {
          const row = this.rows.find(r => r.selectedValue === item.Additive);
          if (row) {
            // Update KV100 value
            if (item.KV100) {
              row.KV100cSt = typeof item.KV100 === 'number' ?
                item.KV100.toFixed(2) : item.KV100;
            }
          }
        }
      });
    }
    this.emitSelectedData();
  }

  // Add method to calculate and send totals
  calculateAndEmitTotals(): void {
    let totalTreatP1 = 0;
    let totalTreatP2 = 0;

    // Calculate Base Oil totals
    this.rows.forEach(row => {
      if (row.selectedValue && row.treat1 && !isNaN(row.treat1)) {
        totalTreatP1 += parseFloat(row.treat1);
      }
      if (row.selectedValue && row.treat2 && !isNaN(row.treat2)) {
        totalTreatP2 += parseFloat(row.treat2);
      }
    });

    // Send totals to the service
    this.sharedService.updateComponentTotals(
      'vm',
      totalTreatP1,
      totalTreatP2
    );
  }

  // Add this method right after calculateAndEmitTotals()
  limitTreatValue(rowIndex: number, field: 'treat1' | 'treat2'): void {
    const value = this.rows[rowIndex][field];

    // If the value is numeric and greater than 100
    if (value && !isNaN(value)) {
      const numValue = parseFloat(value);
      if (numValue > 100) {
        // Limit to 100
        this.rows[rowIndex][field] = '100';
      }
    }

    // Recalculate totals after applying the limit
    this.calculateAndEmitTotals();
  }

  addRow() {
    const newIndex = this.rows.length;

    // Create a new control
    const newControl = new FormControl();
    this.vmControls.push(newControl);

    // Initialize the filtered option
    this.filteredVmOptions[newIndex] = [...this.vmOptions];

    // Set up the filtering observable
    newControl.valueChanges.pipe(
      startWith(''),
      map(value => typeof value === 'string' ? value : value?.label),
      map(name => name ? this._filter(name, newIndex) : this.vmOptions.slice())
    ).subscribe(filtered => this.filteredVmOptions[newIndex] = filtered);

    // Initialize lastValidSelection for this row
    this.lastValidSelections[newIndex] = null;

    // Add the row
    this.rows.push({
      selectedValue: '',
      KV100cSt: '',
      treat1: '',
      treat2: ''
    });
  }

  clearRowData(index: number): void {
    this.rows[index].selectedValue = '';
    this.rows[index].KV100cSt = '';
    this.rows[index].treat1 = '';
    this.rows[index].treat2 = '';

    // Clear the form control as well
    this.vmControls[index].setValue(null);
    this.lastValidSelections[index] = null;
    this.filteredVmOptions[index] = [...this.vmOptions];

    // Update data
    this.calculateAndEmitTotals();
    this.emitSelectedData();
  }

  /**
   * Hydrate VM selections from imported favorite finishedoil_data$.
   * Looks for rows with Fixed_value starting with "VM".
   */
  private async hydrateVmFromImport(): Promise<void> {
    // Clear existing state before importing
    for (let i = this.rows.length - 1; i >= 0; i--) {
      this.clearRowData(i);
    }

    const foData = await firstValueFrom(this.sharedService.finishedoil_data$);
    if (!foData || !Array.isArray(foData) || foData.length === 0) return;
    if (!this.vmOptions || this.vmOptions.length === 0) return;

    const vmRows = foData.filter((row: any) =>
      row.Fixed_value && row.Fixed_value.startsWith('VM')
    );

    for (let i = 0; i < vmRows.length && i < 2; i++) {
      const row = vmRows[i];
      const vmName = row.Name;
      if (!vmName) continue;

      while (this.rows.length <= i) this.addRow();

      const match = this.vmOptions.find(opt => opt.value === vmName);
      if (!match) continue;

      this.vmControls[i].setValue(match, { emitEvent: false });
      this.lastValidSelections[i] = match;
      this.rows[i].selectedValue = vmName;
      if (row.Treat_wt_P1) this.rows[i].treat1 = row.Treat_wt_P1;
      if (row.Treat_wt_P2) this.rows[i].treat2 = row.Treat_wt_P2;
    }

    // Load KV100 data from API
    const vmSelection = this.rows.map(r => r.selectedValue ? r.selectedValue : null);
    if (vmSelection.some(v => v)) {
      this.getVMData(vmSelection);
    }
    this.calculateAndEmitTotals();
    this.emitSelectedData();
  }
}

